import './team-schedule.styles.scss';
import { Link } from 'react-router-dom';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';
import { paybackReason, rivalryOf } from '@shared/rivalry.js';

const POST_TAGS = { conference: 'conf tourn', national: 'tourney', playoffs: 'playoffs' };

// Small icons for the rivalry and payback tags (drawn in currentColor).
const FlameIcon = () => (
    <svg viewBox='0 0 16 16' aria-hidden='true'>
        <path d='M8.6 1c.4 2.2-.6 3.5-1.6 4.7C6 6.9 5 8 5 9.8a3 3 0 0 0 6 .2c0-.8-.3-1.5-.7-2.1 1.5.5 2.7 2 2.7 3.8A5 5 0 0 1 8 16a5 5 0 0 1-5-4.8C3 7 7.4 5.6 8.6 1Z' fill='currentColor' />
    </svg>
);
const TargetIcon = () => (
    <svg viewBox='0 0 16 16' aria-hidden='true'>
        <circle cx='8' cy='8' r='6' fill='none' stroke='currentColor' strokeWidth='2' />
        <circle cx='8' cy='8' r='2.2' fill='currentColor' />
    </svg>
);

// One team's games for the season, with results.
// schedule: summary/schedule doc. teams: summary/teams `teams` map.
// gameLink(gameId) / teamLink(teamId) -> paths. games: show this list
// instead of the whole season (e.g. just recent results). year: the
// season (for payback against last season's eliminator). previewLink(gameId):
// games not played yet link to their matchup preview.
const TeamSchedule = ({ teamId, schedule, teams, gameLink, teamLink, games, year, previewLink }) => {

    const list = games ?? (schedule?.games ?? []).filter(g => g.home === teamId || g.away === teamId);
    if (!list.length) return <p className='meta'>No games.</p>;
    const me = teams?.[teamId];
    // Badge and name (phones hide the name; the badge shows the abbreviation).
    const oppLabel = (opp, oppId) => (
        <>
            <TeamBadge team={opp} size='sm' />
            <span className='sched-label'><TeamName team={opp} id={oppId} nameClass='sched-name' /></span>
        </>
    );
    const payback = (g, oppId) => paybackReason({ teamId, oppId, games: schedule?.games ?? list, day: g.day, payback: me?.payback, year });

    return (
        <ol className='team-schedule'>
            {list.map(g => {
                const home = g.home === teamId;
                const oppId = home ? g.away : g.home;
                const opp = teams?.[oppId];
                const r = g.result;
                const won = r && (home ? r.home > r.away : r.away > r.home);
                const score = r && (home ? `${r.home}-${r.away}` : `${r.away}-${r.home}`);
                return (
                    <li key={g.id} className='sched-row'>
                        <span className='sched-day meta'>Day {g.day}</span>
                        <span className='sched-opp'>
                            <span className='sched-at'>{home ? 'vs' : '@'}</span>
                            {teamLink ?
                                <Link to={teamLink(oppId)} className='sched-team' title={opp?.name}>{oppLabel(opp, oppId)}</Link> :
                                <span className='sched-team' title={opp?.name}>{oppLabel(opp, oppId)}</span>}
                            {g.conf && <span className='conf-tag'>conf</span>}
                            {g.post && <span className='conf-tag post-tag' title={g.label}>{POST_TAGS[g.post]}</span>}
                            {rivalryOf(me, oppId) && <span className='conf-tag rival-tag' title={rivalryOf(me, oppId).name ?? 'Rivalry game'} aria-label='Rivalry game'><FlameIcon /><span className='tag-text'>rival</span></span>}
                            {payback(g, oppId) && <span className='conf-tag payback-tag' title='A rematch after a loss' aria-label='Payback game'><TargetIcon /><span className='tag-text'>payback</span></span>}
                        </span>
                        {r ?
                            <Link to={gameLink(g.id)} className={`sched-result ${won ? 'win' : 'loss'}`}>
                                {won ? 'W' : 'L'} {score}{r.ot ? ' OT' : ''}
                            </Link> :
                            previewLink ?
                                <Link to={previewLink(g.id)} className='sched-result sched-preview' aria-label={`Matchup preview, day ${g.day}`}>Preview</Link> :
                                <span className='sched-result meta'>-</span>
                        }
                    </li>
                )
            })}
        </ol>
    )
}

export default TeamSchedule
